import React, { useState } from 'react';

import { DownOutlined } from '@ant-design/icons/lib';
import { Space } from 'antd';
import styled from 'styled-components';

import Button from 'components/lib/Button';
import Dropdown from 'components/lib/Dropdown';
import { canViewPricingData } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import CommunityViewWidgetModal from './CommunityViewWidgetModal';
import {
  CUSTOM_WIDGET_TYPES,
  GRAPH_WIDGET_TYPES,
  SINGLE_METRIC_WIDGET_TYPES,
  WIDGET_TYPES,
} from '../../../common/Widgets/constants';
import { getWidgetTypeLabel } from '../../../common/Widgets/utils';
import { DEFAULT_WIDGET, WIDGET_MODAL_TYPE } from '../../../common/Widgets/Widget/constants';
import { useCommunityViewContext } from '../CommunityViewContext';
import { getAvailableWidgetOptions } from '../widgetOptions';

const MenuContainer = styled.div`
  display: flex;
  flex-direction: row;
  max-width: calc(100vw - 48px);
  max-height: min(420px, calc(100vh - 180px));
  overflow: auto;
  background: var(--white);
  border: 1px solid var(--line-gray);
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
`;

const Category = styled.div`
  display: flex;
  flex-direction: column;
`;

const CategoryTitle = styled.span`
  font-family: var(--font-bold);
  color: var(--text-secondary);
  padding: 7px 10px;
  background-color: var(--background-gray);
`;

const MenuItem = styled.div`
  width: 240px;
  padding: 7px 10px;
  border-top: 1px solid var(--line-gray);
  cursor: pointer;
  white-space: normal;
  overflow-wrap: break-word;
  line-height: 1.35;

  &:hover {
    background-color: var(--light-gray);
  }
`;

const CategoryList = styled.div`
  display: flex;
  flex-direction: column;

  &.left-column {
    ${MenuItem} {
      border-right: 1px solid var(--line-gray);
    }
  }
  &.right-column {
    ${MenuItem}:last-child {
      border-bottom: 1px solid var(--line-gray);
    }
  }
`;

const CommunityViewAddWidget = () => {
  const [openDropdown, setOpenDropdown] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [widgetType, setWidgetType] = useState<number>(WIDGET_TYPES.MARKET_RATE);
  const { view, community } = useCommunityViewContext();

  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const showPricingData = canViewPricingData(currentUser);
  const permissions = currentUser?.all_permissions;

  const handleSelection = (widgetType: any) => {
    setOpenDropdown(false);

    setOpenModal(true);
    setWidgetType(widgetType);
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  const getWidgetLabel = (type: number) => getWidgetTypeLabel(type, community);

  if (permissions?.indexOf('views.add_widget') === -1) {
    return null;
  }

  let graphWidgets = [
    ...GRAPH_WIDGET_TYPES,
    WIDGET_TYPES.SWOT_PERFORMANCE,
    ...CUSTOM_WIDGET_TYPES,
    WIDGET_TYPES.AMENITIES,
    WIDGET_TYPES.CONTRACT_BENEFITS,
  ];
  let singleMetricWidgets = SINGLE_METRIC_WIDGET_TYPES;
  const { availableGraphOptions, availableSingleOptions } = getAvailableWidgetOptions({
    graphOptions: graphWidgets,
    singleOptions: singleMetricWidgets,
    showPricingData,
    associatedRentalTypes: view?.associated_rental_types,
  });
  graphWidgets = availableGraphOptions;
  singleMetricWidgets = availableSingleOptions;

  const showSingleMetricsSection = showPricingData && singleMetricWidgets.length > 0;

  return (
    <div>
      {openModal && (
        <CommunityViewWidgetModal
          open={openModal}
          onClose={handleClose}
          modalType={WIDGET_MODAL_TYPE.ADD}
          widget={{
            ...DEFAULT_WIDGET,
            type: widgetType,
          }}
        />
      )}

      <Dropdown
        open={openDropdown}
        onOpenChange={(visible) => setOpenDropdown(visible)}
        placement="bottomRight"
        dropdownRender={(menu) => {
          return (
            <MenuContainer>
              <Category>
                <CategoryTitle>GRAPHS AND TABLES</CategoryTitle>
                <CategoryList className={'left-column'}>
                  {graphWidgets.map((type) => (
                    <MenuItem key={type} onClick={() => handleSelection(type)}>
                      {getWidgetLabel(type)}
                    </MenuItem>
                  ))}
                </CategoryList>
              </Category>
              {showSingleMetricsSection && (
                <Category>
                  <CategoryTitle>SINGLE METRICS</CategoryTitle>
                  <CategoryList className={'right-column'}>
                    {singleMetricWidgets.map((type) => (
                      <MenuItem key={type} onClick={() => handleSelection(type)}>
                        {getWidgetLabel(type)}
                      </MenuItem>
                    ))}
                  </CategoryList>
                </Category>
              )}
            </MenuContainer>
          );
        }}
      >
        <Button type="primary">
          <Space align="baseline">
            <span>Add Widget</span>
            <DownOutlined style={{ fontSize: 12 }} />
          </Space>
        </Button>
      </Dropdown>
    </div>
  );
};

export default CommunityViewAddWidget;
